import React, { useState } from 'react';

interface WineSelectionButtonsProps {
  onSelectionClick: (category: string, value: string) => void;
  language?: string;
}

interface SelectionOption {
  label: string;
  value: string;
}

interface SelectionCategory {
  key: string;
  label: string;
  options: SelectionOption[];
}

const WineSelectionButtons: React.FC<WineSelectionButtonsProps> = ({ onSelectionClick, language = 'en' }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const translations = {
    en: {
      popular: 'Popular',
      region: 'Region',
      color: 'Color',
      vintage: 'Vintage',
      grape: 'Grape',
      budget: 'Budget',
      red: 'Red',
      white: 'White',
      rose: 'Rose',
      around: 'Around',
      under: 'Under',
      bestOffer: 'Best offer'
    },
    'zh-CN': {
      popular: '热搜',
      region: '产地',
      color: '葡萄酒颜色',
      vintage: '年份',
      grape: '葡萄品种',
      budget: '预算',
      red: '红酒',
      white: '白酒',
      rose: '桃红酒',
      around: '约',
      under: '低于',
      bestOffer: '最优价格'
    },
    'zh-TW': {
      popular: '熱搜',
      region: '產地',
      color: '葡萄酒顏色',
      vintage: '年份',
      grape: '葡萄品種',
      budget: '預算',
      red: '紅酒',
      white: '白酒',
      rose: '桃紅酒',
      around: '約',
      under: '低於',
      bestOffer: '最優價格'
    }
  };

  const t = translations[language as keyof typeof translations] || translations.en;

  const categories: SelectionCategory[] = [
    {
      key: 'popular',
      label: t.popular,
      options: [
        { label: 'Grower champagne', value: 'grower champagne' },
        { label: 'First growth', value: 'first growth' },
        { label: 'Super Tuscan', value: 'super tuscan' },
        { label: 'Right bank merlot', value: 'right bank merlot' },
        { label: 'Chablis premier cru', value: 'chablis premier cru' },
        { label: 'Napa Chardonnay', value: 'napa chardonnay' }
      ]
    },
    {
      key: 'region',
      label: t.region,
      options: [
        { label: 'Margaux', value: 'Margaux' },
        { label: 'Chambolle Musigny 1er Cru', value: 'Chambolle Musigny 1er Cru' },
        { label: 'Chambertin Grand Cru', value: 'Chambertin Grand Cru' },
        { label: 'Brunello di Montalcino', value: 'Brunello di Montalcino' },
        { label: 'Oregon', value: 'Oregon' },
        { label: 'New Zealand', value: 'New Zealand' },
        { label: 'Priorat', value: 'Priorat' },
        { label: 'Loire Valley', value: 'Loire Valley' }
      ]
    },
    {
      key: 'color',
      label: t.color,
      options: [
        { label: t.red, value: 'red' },
        { label: t.white, value: 'white' },
        { label: t.rose, value: 'rosé' }
      ]
    },
    {
      key: 'vintage',
      label: t.vintage,
      options: [
        { label: '1990s', value: '1990-1999' },
        { label: '2000-2010', value: '2000-2010' },
        { label: '2009, 2010', value: '2009-2010' }
      ]
    },
    {
      key: 'grape',
      label: t.grape,
      options: [
        { label: 'Pinot Noir', value: 'pinot noir' },
        { label: 'Cabernet Sauvignon', value: 'cabernet sauvignon' },
        { label: 'Sauvignon Blanc', value: 'sauvignon blanc' },
        { label: 'Merlot', value: 'merlot' },
        { label: 'Syrah/Shiraz', value: 'syrah' },
        { label: 'Tempranillo', value: 'tempranillo' },
        { label: 'Chardonnay', value: 'chardonnay' },
        { label: 'Riesling', value: 'riesling' },
        { label: 'Sangiovese', value: 'sangiovese' }
      ]
    },
    {
      key: 'budget',
      label: t.budget,
      options: [
        { label: `${t.around} $1000`, value: 'around $1000' },
        { label: `${t.under} $800`, value: 'under $800' },
        { label: '$1000 - $1200', value: '$1000-$1200' },
        { label: t.bestOffer, value: 'best offer' }
      ]
    }
  ];

  const handleCategoryClick = (categoryKey: string) => {
    setActiveCategory(activeCategory === categoryKey ? null : categoryKey);
  };

  const handleOptionClick = (category: string, value: string) => {
    onSelectionClick(category, value);
    setActiveCategory(null);
  };

  return (
    <div style={{
      marginTop: '10px',
    }}>
      {/* Category Buttons */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: activeCategory ? '10px' : '0'
      }}>
        {categories.map((category) => (
          <button
            type="button"
            key={category.key}
            onClick={() => handleCategoryClick(category.key)}
            style={{
              backgroundColor: activeCategory === category.key ? '#B8860B' : '#f5f5f5',
              color: activeCategory === category.key ? 'white' : '#333',
              border: 'none',
              borderRadius: '20px',
              padding: '6px 14px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
          >
            {category.label}
            {activeCategory === category.key && <span style={{ marginLeft: '4px' }}>▼</span>}
          </button>
        ))}
      </div>

      {/* Options Dropdown */}
      {activeCategory && (
        <div style={{
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px'
        }}>
          {categories.find(cat => cat.key === activeCategory)?.options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleOptionClick(activeCategory, option.value)}
              style={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: '#555'
              }}
              onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.borderColor = '#B8860B';
              }}
              onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#ddd';
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export default WineSelectionButtons;